import { Component, ChangeDetectionStrategy, input, signal, output, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InputText } from 'primeng/inputtext';
import { AutoComplete } from 'primeng/autocomplete';
import { Bind } from 'primeng/bind';
import { NgkitSearchItem } from '../interfaces/ngkit-search-item';

/**
 * Search component that wraps the PrimeNG text input with an agreed set of icons.
 * Provides a convenient way for developers to implement search functionality with a predefined set of search items.
 *
 * Features:
 * - Accepts optional searchable items with id, name, and type properties
 * - Switches between text input (no items) and autocomplete (with items)
 * - Emits search submissions when user presses Enter in text mode
 * - Supports dark mode styling
 * - Width customization via standard CSS classes (ngkit-width-xs, sm, md, lg, full)
 * - Keyboard accessible with proper ARIA attributes
 * @example
 * // Text input mode (no items)
 * <ngkit-search
 *   (searchSubmitted)="onSearch($event)"
 *   [isContrast]="isDarkMode()"
 * ></ngkit-search>
 * @example
 * // Autocomplete mode with search items
 * <ngkit-search
 *   [searchItems]="items()"
 *   (itemSelected)="onItemSelected($event)"
 *   class="ngkit-width-md"
 * ></ngkit-search>
 */
@Component({
  selector: 'ngkit-search',
  templateUrl: './ngkit-search.component.html',
  styleUrl: './ngkit-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, InputText, AutoComplete, Bind],
})
export class NgkitSearchComponent {
  private readonly sanitizer = inject(DomSanitizer);
  /**
   * An array of search items that can be indexed and searched.
   * Each item includes an id, name, and type for identification and categorization.
   * When provided, switches component to autocomplete mode.
   * When empty, uses text input mode.
   * @default []
   */
  readonly searchItems = input<NgkitSearchItem[]>([]);

  /**
   * Enables dark mode styling for the search box.
   * When true, applies the 'ngkit-search-box--dark' class instead of 'ngkit-search-box'.
   * @default false
   */
  readonly isContrast = input<boolean>(false);

  /**
   * Placeholder text for the search input.
   * Displayed in both text input and autocomplete modes.
   * @default 'Search...'
   */
  readonly placeholder = input<string>('Search...');

  /**
   * Initial value to populate the search input when the component loads.
   * If provided, the search box will be pre-filled with this value.
   * @default ''
   */
  readonly prepopulatedSearchQuery = input<string>('');

  /**
   * Emits the search query when the user presses Enter in text input mode.
   * The event contains the current search query string.
   */
  readonly searchSubmitted = output<string>();

  /**
   * Emits when a user selects an item from the autocomplete list.
   * The event contains the selected NgkitSearchItem.
   */
  readonly itemSelected = output<NgkitSearchItem>();

  /**
   * Emits whenever the search string is updated.
   * The event contains the current search query string.
   */
  readonly searchUpdated = output<string>();

  /**
   * Emits when the search is cleared.
   */
  readonly searchCleared = output<void>();

  /**
   * The current search query value.
   * Tracks the user's input in the search box.
   * Intelligently synchronized with prepopulatedSearchQuery:
   * - Updates only if current query matches the previous prepopulated value
   * - Preserves user-entered values that differ from prepopulated values
   * - Will not auto-populate when search is cleared
   */
  protected readonly searchQuery = signal<string>('');

  /**
   * Tracks the previous prepopulated value to detect user changes.
   * When prepopulatedSearchQuery changes, we compare the new value with this previous value
   * stored in the current searchQuery to determine if the user has manually changed their input.
   * If the current searchQuery matches this previous prepopulated value, it means the user
   * hasn't manually overridden it, so we can safely update to the new prepopulated value.
   * @internal
   */
  private readonly previousPrepopulatedValue = signal<string>('');

  /**
   * Filtered autocomplete suggestions based on the current search query.
   * Updates dynamically as the user types.
   */
  protected readonly filteredItems = signal<NgkitSearchItem[]>([]);

  /**
   * Tracks whether a search submission is in progress.
   * When true, displays a loading spinner instead of the input/autocomplete.
   * @default false
   */
  protected readonly isSubmitting = signal<boolean>(false);

  /**
   * Computed signal that determines if autocomplete mode should be used.
   * True when searchItems array is not empty.
   */
  protected readonly useAutocomplete = computed(() => this.searchItems().length > 0);

  /**
   * Highlights matching portions of text by wrapping them in <strong> tags.
   * Performs case-insensitive matching and returns sanitized HTML.
   * @param text The text to highlight matches in
   * @param query The search query string to highlight
   * @returns SafeHtml with matching portions wrapped in <strong> tags
   */
  protected readonly highlightMatches = (text: string, query: string): SafeHtml => {
    if (!query || !text) {
      return this.sanitizer.sanitize(1, text) || '';
    }

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const highlighted = text.replace(regex, '<strong>$1</strong>');

    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  };

  constructor() {
    // Initialize filteredItems whenever searchItems changes
    effect(() => {
      this.filteredItems.set(this.searchItems());
    });

    // Intelligently synchronize search query with prepopulated value
    // Only updates if current query matches the previous prepopulated value (user hasn't manually changed it)
    effect(() => {
      const prepopulated = this.prepopulatedSearchQuery();
      const currentQuery = this.searchQuery();
      const previousPrepopulated = this.previousPrepopulatedValue();

      // Only update if current query matches previous prepopulated value (user hasn't manually changed it)
      if (currentQuery === previousPrepopulated) {
        this.searchQuery.set(prepopulated);
      }

      // Always track the current prepopulated value for next comparison
      this.previousPrepopulatedValue.set(prepopulated);
    });

    // Filter items when search query changes (for initial query and user input)
    effect(() => {
      const query = this.searchQuery();
      if (this.useAutocomplete()) {
        const filtered = query
          ? this.searchItems().filter(
              (item) =>
                item.name.toLowerCase().includes(query.toLowerCase()) ||
                item.type.toLowerCase().includes(query.toLowerCase()) ||
                item.id.toLowerCase().includes(query.toLowerCase()),
            )
          : this.searchItems();
        this.filteredItems.set(filtered);
      }
    });
  }

  /**
   * Clears the current search query.
   * Resets the search input to an empty state.
   */
  protected readonly clearSearch = (): void => {
    this.searchQuery.set('');
    this.filteredItems.set(this.searchItems());
    this.searchCleared.emit();
  };

  /**
   * Updates the search query and emits the searchUpdated output.
   * @param value The new search query value
   */
  protected readonly updateSearchQuery = (value: string): void => {
    this.searchQuery.set(value);
    this.searchUpdated.emit(value);
  };

  /**
   * Filters search items based on the query string from completeMethod event.
   * Compares against item's name, type and id properties (case-insensitive).
   * Updates filteredItems signal with matching results.
   * @param event The completeMethod event
   * @param event.query The search query string to filter items by
   */
  protected readonly filterItems = (event: { query: string }): void => {
    const query = event.query || '';
    this.updateSearchQuery(query);
    const filtered = query
      ? this.searchItems().filter(
          (item) =>
            item.name.toLowerCase().includes(query.toLowerCase()) ||
            item.type.toLowerCase().includes(query.toLowerCase()) ||
            item.id.toLowerCase().includes(query.toLowerCase()),
        )
      : this.searchItems();
    this.filteredItems.set(filtered);
  };

  /**
   * Handles search submission when user presses Enter.
   * If filtered items are available, selects the first item from the list.
   * Otherwise, emits the current search query and clears the input.
   */
  protected readonly onSearchSubmit = (): void => {
    if (this.filteredItems().length > 0) {
      this.onItemSelect(this.filteredItems()[0]);
    } else {
      this.isSubmitting.set(true);
      this.searchSubmitted.emit(this.searchQuery());
      this.searchQuery.set('');
      setTimeout(() => {
        this.isSubmitting.set(false);
      }, 500);
    }
  };

  /**
   * Handles autocomplete item selection.
   * Emits the selected item and clears the search query.
   * @param event The autocomplete select event containing the selected item.
   */
  protected readonly onItemSelect = (event: { value: NgkitSearchItem } | NgkitSearchItem): void => {
    let selectedItem: NgkitSearchItem | null = null;

    if ('value' in event) {
      selectedItem = event.value;
    } else if (event && 'name' in event && 'type' in event && 'id' in event) {
      selectedItem = event;
    }

    if (selectedItem) {
      this.isSubmitting.set(true);
      this.itemSelected.emit(selectedItem);
      this.searchQuery.set('');
      setTimeout(() => {
        this.isSubmitting.set(false);
      }, 500);
    }
  };
}
